<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { useRouter } from 'vue-router'
import { useForm11 } from '@/composables/useForm11'
import MainLayout from '@/components/layout/MainLayout.vue'

interface Question {
  question: string
  form: string
}

const questions: Question[] = [
  {
    question: 'Do you have an assistance dog?',
    form: '/form11/assistance-dog-allowance',
  },
  {
    question: 'Have you paid for permanent health/medical insurance?',
    form: '/form11/permanent-health-benefit',
  },
  { question: 'Do you have a guide dog?', form: '/form11/guide-dog-allowance' },
  {
    question: 'Have you paid any nursing home expenses?',
    form: '/form11/nursing-home-expenses',
  },
  {
    question: "Do you qualify for a blind person's tax credit?",
    form: '/form11/blind-person-tax-credit',
  },
  {
    question: 'Have you incurred health expenses eligible for tax relief?',
    form: '/form11/health-expenses',
  },
  {
    question: 'Did you claim medical insurance relief this year?',
    form: '/form11/medical-insurance-relief',
  },
  {
    question: 'Have you employed a carer for a dependent or family member?',
    form: '/form11/dependant-relevant-tax-credit',
  },
  {
    question: 'Did you pay rent that qualifies for a rent tax credit?',
    form: '/form11/rent-tax-credit',
  },
  {
    question:
      'Did you make any maintenance payments that qualify for tax relief?',
    form: '/form11/maintenance-payment-relief',
  },
  {
    question: 'Did you contribute to a Retirement Annuity Contract or a PRSA?',
    form: '/form11/retirement-annuity-relief',
  },
  {
    question: 'Did you make any Additional Voluntary Contributions this year?',
    form: '/form11/additional-voluntary-contributions',
  },
  {
    question: 'Do you have incapacitated children?',
    form: '/form11/incapacitated-child-tax-credit',
  },
  {
    question:
      'Are you claiming tax relief on retirement relief for sportspersons?',
    form: '/form11/personal-retirement-savings',
  },
  {
    question: 'Are you claiming tax relief on educational tuition fees?',
    form: '/form11/tuition-fees',
  },
  {
    question:
      'Are you claiming the dependent relative tax credit or single person child carer credit?',
    form: '/form11/single-person-child-carer-credit',
  },
  {
    question: 'Have you spent days at sea on a registered fishing vessel?',
    form: '/form11/fisher-tax-credit',
  },
  {
    question: 'Have you worked as a seafarer outside the State?',
    form: '/form11/seafarer-allowance',
  },
  {
    question:
      'Are you a permanent member of the Irish Naval Service with at least 80 days at sea?',
    form: '/form11/naval-service-credit',
  },
  {
    question:
      'Are you claiming Owner Occupier Relief for a residential property in a designated area?',
    form: '/form11/owner-occupier-relief',
  },
  {
    question:
      'Do you have a Home Renovation Incentive (HRI) tax credit due for 2023?',
    form: '/form11/home-renovation-incentive',
  },
  {
    question: 'Have you made donations to approved sports bodies in 2023?',
    form: '/form11/approved-sports-bodies',
  },
  {
    question:
      'Have you invested in eligible shares under the Start-up Relief for Entrepreneurs (SURE) scheme?',
    form: '/form11/startup-relief-entrepreneurs',
  },
  {
    question:
      'Have you invested in shares under the Employment Investment Incentive (EII) scheme?',
    form: '/form11/employment-investment-incentive',
  },
  {
    question:
      'Are you claiming Mortgage Interest Tax Credit for a qualifying property?',
    form: '/form11/mortgage-interest-tax-credit',
  },
  {
    question:
      'Are you a PAYE worker who wants to claim the Employee (PAYE) Tax Credit?',
    form: '/form11/employee-tax-credit',
  },
  {
    question:
      'Are you self-employed or a proprietary director who wants to claim the Earned Income Tax Credit?',
    form: '/form11/earned-income-tax-credit',
  },
  {
    question: 'Do you have to declare additional income?',
    form: '/form11/income-selection',
  },
]

const router = useRouter()
const form11 = useForm11()
const currentQuestion = ref(0)
const selectedForms = ref<string[]>([])
const answeredQuestions = ref<string[]>([])
const isComplete = ref(false)

watch(isComplete, () => {
  if (isComplete.value && selectedForms.value.length > 0) {
    form11.setup(selectedForms.value)
  } else if (isComplete.value) {
    router.push('/tax-refund-options')
  }
})

const handleYes = () => {
  const currentForm = questions[currentQuestion.value].form
  if (!selectedForms.value.includes(currentForm)) {
    selectedForms.value.push(currentForm)
  }

  if (currentQuestion.value < questions.length - 1) {
    currentQuestion.value++
  } else {
    isComplete.value = true
  }
  answeredQuestions.value.push(currentForm)
}

const handleNo = () => {
  const currentForm = questions[currentQuestion.value].form
  selectedForms.value = selectedForms.value.filter(
    (form) => form !== currentForm,
  )

  if (currentQuestion.value < questions.length - 1) {
    currentQuestion.value++
  } else {
    isComplete.value = true
  }
  answeredQuestions.value.push(currentForm)
}

const goToPrevious = () => {
  if (currentQuestion.value > 0) {
    currentQuestion.value--
  }
}

const goToNext = () => {
  if (currentQuestion.value < questions.length - 1) {
    currentQuestion.value++
  }
}
</script>

<template>
  <MainLayout>
    <div class="max-w-3xl mx-auto p-6">
      <!-- Question Navigation -->
      <div class="mb-6">
        <div class="flex justify-center items-center gap-5">
          <Button
            variant="ghost"
            @click="goToPrevious"
            :disabled="currentQuestion === 0"
          >
            Prev
          </Button>
          <span class="text-lg sm:text-xl">
            Question {{ currentQuestion + 1 }} / {{ questions.length }}
          </span>
          <Button
            variant="ghost"
            @click="goToNext"
            :disabled="currentQuestion === questions.length - 1"
          >
            Next
          </Button>
        </div>
      </div>

      <!-- Question Card -->
      <div
        class="flex items-center justify-center bg-primary text-white px-20 h-52 rounded-3xl mb-6 w-full"
      >
        <h2 class="text-xl sm:text-2xl font-bold text-center">
          {{ questions[currentQuestion].question }}
        </h2>
      </div>

      <!-- Answer Buttons -->
      <div class="flex justify-center gap-5">
        <Button
          class="w-full"
          :variant="
            answeredQuestions.includes(questions[currentQuestion].form) &&
            selectedForms.includes(questions[currentQuestion].form)
              ? 'default'
              : 'outline'
          "
          @click="handleYes"
        >
          Yes
        </Button>
        <Button
          class="w-full"
          :variant="
            answeredQuestions.includes(questions[currentQuestion].form) &&
            !selectedForms.includes(questions[currentQuestion].form)
              ? 'default'
              : 'outline'
          "
          @click="handleNo"
        >
          No
        </Button>
      </div>
    </div>
  </MainLayout>
</template>

<style scoped>
.bg-primary {
  background-color: #4a579b;
}
</style>
